'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'motion/react'

import type { RouterOutputs } from '@package/api'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatCurrency } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

type VoucherData = RouterOutputs['price']['getActiveVouchers'][number]

export interface VoucherListProps {
  className?: string
  priceBeforeVoucher: number
  selectedVoucherId: string | undefined | null
  onSelectVoucher: (voucher: VoucherData | null) => void
  selectedPriceId: string
  type: 'web' | 'school'
  disabled?: boolean
  /**
   * Which checkout is asking. `CHECKOUT` (the default) lists package vouchers; `ADD_ON` lists the
   * codes written for mid-term seats, which are a separate pool — the server refuses one spent on
   * the other, so the two lists must never mix.
   */
  scope?: 'CHECKOUT' | 'ADD_ON'
}

/**
 * What this voucher would take off `amount`, by the same rules the checkout form and the server
 * apply: a percentage is capped by `maximumValue` when it has one, and no voucher can discount more
 * than the order is worth.
 */
function discountFor(voucher: VoucherData, amount: number): number {
  const value = Number(voucher.value)

  const raw =
    voucher.type === 'PERCENTAGE'
      ? // `!== null`, not truthiness: a `maximumValue` of 0 is a cap of zero, and reading it as
        // falsy turns it into "no cap at all".
        voucher.maximumValue !== null && voucher.maximumValue !== undefined
        ? Math.min(amount * (value / 100), voucher.maximumValue)
        : amount * (value / 100)
      : value

  return Math.floor(Math.min(raw, amount))
}

type EligibleVoucher = VoucherData & {
  notYetMinimumTransaction: boolean
  isSpecial: boolean
  isDifferentPrice: boolean
}

export function VoucherList({
  className,
  priceBeforeVoucher,
  selectedVoucherId,
  onSelectVoucher,
  selectedPriceId,
  type,
  disabled = false,
  scope = 'CHECKOUT',
}: VoucherListProps) {
  const trpc = useSharedTRPC()

  const authClient = useAuthClient()

  const { data: activeOrganization } = authClient.useActiveOrganization()

  const { data } = useQuery({
    ...trpc.price.getActiveVouchers.queryOptions({ scope }),
    staleTime: 0,
    refetchInterval: 1000 * 60 * 60,
  })

  const eligibleVoucher = useMemo(() => {
    if (!data || !activeOrganization) return []

    const resp = data
      .filter((voucher) => {
        const isAllUsers = voucher.voucherToOrganizations.length === 0

        const isForUser =
          isAllUsers ||
          voucher.voucherToOrganizations.some(
            (voucherToOrganization) => voucherToOrganization.organizationId === activeOrganization.id,
          )

        /**
         * Organization scope is the only filter left. `Price.type` used to gate this too — a
         * family saw only vouchers attached to FAMILY rows — but schools and families share one
         * rate card now, so a family is routinely charged a SCHOOL-tagged tier row. Under the old
         * test its voucher matched neither group and vanished from the list entirely, while the
         * server would have honoured it.
         *
         * Whether a voucher covers the row actually selected is `isDifferentPrice` below, which
         * asks that question directly instead of guessing from a legacy label.
         */
        return isForUser
      })
      .map((voucher) => ({
        ...voucher,
        notYetMinimumTransaction: voucher.minimumTransaction
          ? voucher.id === selectedVoucherId
            ? false
            : voucher.minimumTransaction > priceBeforeVoucher
          : false,
        isSpecial: voucher.voucherToOrganizations.length > 0,
        /**
         * Only meaningful for a package purchase. An add-on is priced from the pools it joins and
         * names no rate-card row, so `voucher_to_price` cannot narrow it — `scope` already did.
         */
        isDifferentPrice:
          scope === 'CHECKOUT' &&
          voucher.voucherToPrices.length > 0 &&
          voucher.voucherToPrices.every((voucherToPrice) => voucherToPrice.priceId !== selectedPriceId),
      }))
      .sort((a, b) => (b.isSpecial ? 1 : 0) - (a.isSpecial ? 1 : 0))

    return resp
  }, [data, activeOrganization, priceBeforeVoucher, selectedPriceId, selectedVoucherId, type, scope])

  /**
   * The voucher that takes the most off THIS order, or null when nothing does.
   *
   * Comparing face values would be wrong in both directions: a 10% voucher beats a Rp250.000 one on
   * a large order and loses on a small one, and a percentage voucher with `maximumValue` stops
   * growing at its cap. So each candidate is priced against the current subtotal, exactly as
   * `calculateVoucherDiscount` prices the selected one.
   *
   * Only usable vouchers compete — a voucher the order cannot claim yet is not "the best".
   */
  const bestVoucherId = useMemo(() => {
    let best: { id: string; amount: number } | null = null

    for (const voucher of eligibleVoucher) {
      if (voucher.notYetMinimumTransaction || voucher.isDifferentPrice) continue

      const amount = discountFor(voucher, priceBeforeVoucher)
      if (amount > 0 && (!best || amount > best.amount)) best = { id: voucher.id, amount }
    }

    return best?.id ?? null
  }, [eligibleVoucher, priceBeforeVoucher])

  if (eligibleVoucher.length === 0) return null

  // Usable now, or shown greyed out with the reason. Every eligible voucher lands in exactly one of
  // the two — the `Price.type` test that used to gate both could drop a voucher out of BOTH, which
  // is how a valid discount disappeared from the list without explanation.
  const enabledGroupVoucher = eligibleVoucher.filter(
    (voucher) => !voucher.notYetMinimumTransaction && !voucher.isDifferentPrice,
  )
  const disabledGroupVoucher = eligibleVoucher.filter(
    (voucher) => voucher.notYetMinimumTransaction || voucher.isDifferentPrice,
  )

  return (
    <div
      className={cn(
        'srd:flex srd:flex-col srd:gap-y-3 srd:w-full',
        disabled && 'srd:opacity-50 srd:cursor-not-allowed srd:pointer-events-none',
        'srd:bg-neutral-white srd:p-4 srd:rounded-xl',
        className,
      )}
    >
      <h6 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black srd:text-center')}>
        Gunakan voucher?
      </h6>

      {enabledGroupVoucher.length > 0 && (
        <div className='srd:flex srd:flex-col srd:gap-y-2'>
          {enabledGroupVoucher.map((voucher, index) => (
            <VoucherCard
              type={type}
              key={voucher.id}
              voucher={voucher}
              isSelected={selectedVoucherId === voucher.id}
              isBest={enabledGroupVoucher.length > 1 && voucher.id === bestVoucherId}
              scope={scope}
              onClick={() => {
                const isCurrentlySelected = selectedVoucherId === voucher.id
                onSelectVoucher(isCurrentlySelected ? null : voucher)
              }}
              index={index}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {disabledGroupVoucher.length > 0 && (
        <Accordion className='srd:w-full'>
          <AccordionItem value='item-1'>
            <AccordionTrigger className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
              {disabledGroupVoucher.length} voucher belum memenuhi syarat
            </AccordionTrigger>
            <AccordionContent className='srd:pt-0'>
              <div className='srd:flex srd:flex-col srd:gap-y-2'>
                {disabledGroupVoucher.map((voucher, index) => (
                  <VoucherCard
                    key={voucher.id}
                    type={type}
                    voucher={voucher}
                    isSelected={selectedVoucherId === voucher.id}
                    scope={scope}
                    onClick={() => {
                      const isCurrentlySelected = selectedVoucherId === voucher.id
                      onSelectVoucher(isCurrentlySelected ? null : voucher)
                    }}
                    index={index}
                    disabled={true}
                    actionable={false}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  )
}

interface VoucherCardProps {
  voucher: EligibleVoucher
  isSelected: boolean
  onClick: () => void
  index: number
  disabled?: boolean
  type: 'web' | 'school'
  /** Whether the card renders the Gunakan/Batalkan action. Ineligible cards stay non-actionable. */
  actionable?: boolean
  /** Marks the voucher worth the most on this order. At most one card in the list carries it. */
  isBest?: boolean
  scope?: 'CHECKOUT' | 'ADD_ON'
}

function VoucherCard({
  voucher,
  isSelected,
  onClick,
  index,
  disabled,
  type,
  actionable = true,
  isBest = false,
  scope = 'CHECKOUT',
}: VoucherCardProps) {
  const trpc = useSharedTRPC()

  const { data: allPriceData } = useQuery({
    ...trpc.price.getActivePrices.queryOptions({ type: type === 'web' ? 'FAMILY' : 'SCHOOL' }),
    staleTime: 0,
  })

  const discountText =
    voucher.type === 'PERCENTAGE'
      ? `Diskon ${Number(voucher.value)}%`
      : `Diskon ${formatCurrency(Number(voucher.value))}`

  const minimumTransactionText = voucher.minimumTransaction
    ? `Minimum transaksi: ${formatCurrency(voucher.minimumTransaction)}`
    : 'Minimum transaksi: -'

  const maximumValueText = voucher.maximumValue ? `Batas maks. ${formatCurrency(voucher.maximumValue)}` : null

  /**
   * Which packages the voucher covers, in TERMS rather than rate-card rows.
   *
   * Volume pricing turned one package into four rows — "12 bulan" exists once per seat tier — so
   * listing rows printed "paket 12 bln, 12 bln, 12 bln, 12 bln". A buyer picks a term, never a
   * tier, so the terms are what the line names, deduplicated and shortest first.
   *
   * "semua paket" when the voucher covers every term on offer. Comparing terms, not row counts:
   * a voucher attached to all four tiers of all three terms covers everything a buyer can pick,
   * and a row-count test would only agree by accident.
   */
  const applicabilityText = (() => {
    // An add-on voucher is scoped, not narrowed: it covers mid-term seats and nothing else, whatever
    // the rate card says.
    if (scope === 'ADD_ON') return 'tambah kursi'

    if (voucher.voucherToPrices.length === 0) return 'semua paket'

    const relevantPrices = voucher.voucherToPrices.filter((voucherToPrice) =>
      type === 'web' ? voucherToPrice.price.type === 'FAMILY' : voucherToPrice.price.type === 'SCHOOL',
    )

    if (relevantPrices.length === 0) return 'semua paket'

    const durations = [...new Set(relevantPrices.map(({ price }) => price.duration))].sort((a, b) => a - b)

    if (allPriceData && allPriceData.length > 0) {
      const offeredDurations = new Set(allPriceData.map((price) => price.duration))

      if (offeredDurations.size === durations.length && durations.every((duration) => offeredDurations.has(duration))) {
        return 'semua paket'
      }
    }

    return `paket ${durations.map((duration) => `${duration} bulan`).join(', ')}`
  })()

  // One wrapped line rather than a stacked column: three short facts in a column read as a list of
  // conditions to work through, and the card is a thing to accept or ignore.
  const metaText = [maximumValueText, `Berlaku untuk: ${applicabilityText}`, minimumTransactionText]
    .filter((part): part is string => Boolean(part))
    .join(' • ')

  const actionLabel = isSelected ? 'Batalkan' : 'Gunakan'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: disabled ? 0.5 : 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className={cn(
        'srd:w-full srd:p-4 srd:rounded-xl srd:border-2 srd:transition-all',
        'srd:flex srd:items-center srd:justify-between srd:gap-x-4',
        disabled && 'srd:opacity-50 srd:cursor-not-allowed srd:bg-neutral-white',
        !disabled && 'srd:bg-neutral-white',
        isSelected && !disabled && 'srd:border-blue-100 srd:bg-blue-10',
        !isSelected && !disabled && 'srd:border-grey-80 srd:bg-neutral-white',
      )}
    >
      <div className='srd:flex srd:min-w-0 srd:flex-1 srd:flex-col srd:gap-y-1'>
        <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-wrap'>
          <span
            className={cn(
              typographyVariants({ variant: 'label-sm' }),
              voucher.isSpecial ? 'srd:text-yellow-100' : 'srd:text-purple-100',
            )}
          >
            {voucher.name}
          </span>

          {isBest && (
            <Badge className='srd:bg-purple-100 srd:text-neutral-white srd:text-[0.625rem] srd:px-3 srd:py-0.25 srd:hover:bg-purple-100/80'>
              Terbaik
            </Badge>
          )}

          {isSelected && (
            <Badge className='srd:bg-blue-100 srd:text-neutral-white srd:text-[0.625rem] srd:px-3 srd:py-0.25 srd:hover:bg-blue-100/80'>
              Dipilih
            </Badge>
          )}
        </div>

        <h3 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>{discountText}</h3>

        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{metaText}</p>
      </div>

      {actionable && (
        <Button
          variant='ghost'
          size='sm'
          disabled={disabled}
          onClick={onClick}
          className='srd:h-auto srd:shrink-0 srd:px-0 srd:py-0'
        >
          {actionLabel}
        </Button>
      )}
    </motion.div>
  )
}
